/**
 * THE REPLY, ON THE TICKET (P37a, Roman's decision K2)
 *
 * =========================================================================
 * WHY THE TICKET SCREEN NEEDED THIS
 * =========================================================================
 * `/tickets/:n` had exactly two things to say about the outbound half of a
 * request: a green delivery-proof card once a mail had gone out, and a "Compose
 * draft" button while none existed. Everything between those two — written,
 * edited, approved, held by the recipient policy, refused — showed nothing.
 *
 * So Katia approved draft 46 on `DRA-2026-00053`, came back to the ticket, and
 * found it unchanged: same status word, no draft, no trace of her decision. Her
 * mail of 2026-09-28 asks how to approve a request. She had.
 *
 * =========================================================================
 * A LINK, AND DELIBERATELY NO APPROVE BUTTON
 * =========================================================================
 * Decision K2. `/approvals` shows the wording, the document, the warnings and
 * the C9 signature — the things somebody is actually approving. A second
 * approve control on a screen that shows none of them would be a click without
 * a reading, which is the thing `bulkApprovable` exists to prevent one screen
 * over. One place approves.
 *
 * Every word here comes from `lib/labels.ts` and every sentence about a held
 * send comes from the SERVER (`sendState.heldExplanation` is the audit row
 * `drafts/approve.ts` wrote, naming the actual address and the actual
 * allowlist). This file writes no prose about a state.
 */
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from './ui/Card';
import { Badge, type Tone } from './ui/Badge';
import { Button, TEXT_LINK } from './ui/Button';
import { useToast } from './ui/Toast';
import { useStartReply } from '../hooks/useTicketsApi';
import {
    DRAFT_TYPE_LABELS, DRAFT_WARNING_LABELS, SEND_STATE_LABELS, labelFor, tooltipFor,
} from '../lib/labels';
import type { Ticket, TicketCurrentDraft, TicketSendState } from '../types/domain';
import { isoDateTime } from '../lib/dates';

/**
 * The tint per state. `err` is a Badge tone and not a StatusPill one, which is
 * why the state is a Badge here: a failed send is not a status the DRA has, and
 * §8 will not let a pill carry it.
 */
const STATE_TONE: Record<string, Tone> = {
    None: 'neutral',
    Pending: 'info',
    ApprovedNotSent: 'warn',
    Sent: 'done',
    Failed: 'err',
    Rejected: 'neutral',
};

/** A glyph beside every tint, because colour never works alone (§8). */
const STATE_GLYPH: Record<string, string> = {
    None: '·',
    Pending: '◷',
    ApprovedNotSent: '⏸',
    Sent: '✓',
    Failed: '▲',
    Rejected: '✕',
};

function stamp(iso: string | null): string {
    return isoDateTime(iso);
}

export function ReplyStateBadge({ state }: { state: string }) {
    const tone = STATE_TONE[state] ?? 'neutral';
    return (
        /*
         * P39: a NEUTRAL badge carries no glyph (§8, P38's rule). `Badge` applies
         * that itself for `glyphName`, but a node passed as `glyph` wins on
         * purpose — these six marks are not the seven state glyphs — so the rule
         * has to be applied here. Measured before the fix: "No reply written"
         * rendered as "·No reply written", a grey dot that read as a typo,
         * because there was no colour for it to be backing up.
         */
        <Badge tone={tone} glyph={tone === 'neutral' ? undefined : STATE_GLYPH[state]} title={tooltipFor(SEND_STATE_LABELS, state)}>
            {labelFor(SEND_STATE_LABELS, state)}
        </Badge>
    );
}

/**
 * The three states in which the agent may still be asked to write the reply.
 *
 * Read off `compose-draft.ts`'s own guard and not invented here: it answers 409
 * when the ticket already carries a draft in `PendingApproval`, `Approved` or
 * `Sent`. `Rejected` and `Failed` are exactly the two it does NOT refuse — a
 * rejection is the state P24 built the pause for, and pressing Compose is the
 * human action that lifts it.
 */
const COMPOSABLE_SEND_STATES = new Set(['None', 'Rejected', 'Failed']);

/**
 * Shown while there is a draft and the mail has NOT gone out. Once it has,
 * `DeliveryProofPanel` is the card that says so, in more detail and in green —
 * two cards about one sent mail would be two sources for one fact.
 *
 * =========================================================================
 * P39: THE COMPOSE CONTROL CAME INTO THIS CARD, AND WHY
 * =========================================================================
 * `Compose draft` sat in a card of its own directly beneath this one, and the
 * two disagreed. Measured on `DRA-2026-00053`: the screen drew **two primary
 * buttons below the facts** — this card's *Write a reply to the customer* and
 * that card's *Compose draft* — both of which produce the same thing, a draft
 * in Approvals. §7's rule is one primary per view, and P37b's handoff already
 * claimed this button was "the only primary on that screen below the facts",
 * which was not true the day it was written.
 *
 * Worse, that card was drawn on `canEdit && composable && delivery.length === 0`
 * — so a ticket with a draft already waiting for approval offered a button whose
 * endpoint answers *"already has draft N"* every single time. A control that can
 * only fail is the dead end this plan exists to remove.
 *
 * So the compose control is **passed in and placed here**, in the one card that
 * answers "what is the reply, and where is it", and only in the states where the
 * endpoint can act. The mutation, the gates and the toasts stay where they were
 * (`TicketDetail`); this card decides nothing about composing except WHERE the
 * button belongs and WHEN there is any point in drawing it.
 */
export function ReplyCard({ ticket, canReply = false, composeAction }: {
    ticket: Ticket;
    canReply?: boolean;
    /** The Compose button and its sentence. Drawn only where compose can work. */
    composeAction?: ReactNode;
}) {
    const draft: TicketCurrentDraft | null | undefined = ticket.currentDraft;
    const sendState: TicketSendState | undefined = ticket.sendState;
    if (!sendState || sendState.state === 'Sent') return null;

    const compose = COMPOSABLE_SEND_STATES.has(sendState.state) ? composeAction : null;

    // P37b. "No draft at all" used to render nothing — which is the state Katia
    // was looking at when she asked where to write a reply. One card answers
    // "what is the reply, and where is it" in every state, including none.
    if (!draft) return <NoReplyCard ticket={ticket} canReply={canReply} composeAction={compose} />;

    const held = sendState.state === 'ApprovedNotSent';

    return (
        <Card
            title="Reply"
            subtitle={`${labelFor(DRAFT_TYPE_LABELS, draft.draftType)} · draft ${draft.id} · written ${stamp(draft.createdAt)}`}
            action={<ReplyStateBadge state={sendState.state} />}
        >
            {/* The server's own sentence about the hold. Not rewritten here: it
                names the address and the allowlist of THIS draft, which a static
                string cannot, and it is the same sentence /approvals shows. */}
            {held && sendState.heldExplanation && (
                <p className="rounded-ctl border border-warn-edge bg-warn-50 px-2.5 py-2 text-xs text-warn-ink">
                    <strong>{SEND_STATE_LABELS.ApprovedNotSent.label}.</strong>{' '}
                    {sendState.heldExplanation}
                </p>
            )}

            <dl className={`grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs ${held && sendState.heldExplanation ? 'mt-3' : ''}`}>
                <dt className="text-muted">Subject</dt>
                <dd className="min-w-0 truncate text-body" title={draft.subject}>{draft.subject}</dd>

                <dt className="text-muted">To</dt>
                <dd className="min-w-0 truncate text-body">{draft.toAddresses.join(', ') || '—'}</dd>

                <dt className="text-muted">Document</dt>
                <dd className="min-w-0 truncate text-body">
                    {draft.chosenDocument
                        ? <span title={draft.chosenDocument.fileName}>
                            {draft.chosenDocument.fileName}
                            {draft.chosenDocument.language ? ` · ${draft.chosenDocument.language}` : ''}
                            {draft.chosenDocument.revisionDate ? ` · ${draft.chosenDocument.revisionDate}` : ''}
                        </span>
                        : <span className="text-muted">none attached</span>}
                </dd>

                {draft.reviewedBy && (
                    <>
                        <dt className="text-muted">Approved</dt>
                        <dd className="min-w-0 truncate text-body">
                            {draft.reviewedBy} · {stamp(draft.reviewedAt)}
                            {draft.editedBeforeSend ? ' · wording edited before approval' : ''}
                        </dd>
                    </>
                )}
            </dl>

            {/* The warning list is the server's judgement, the same one
                /approvals reads (`drafts/warnings.ts`). Counted, not re-judged. */}
            {draft.warnings.length > 0 && (
                <ul className="mt-3 space-y-1">
                    {draft.warnings.map((w, i) => (
                        <li key={`${w.code}-${i}`}>
                            <Badge
                                tone={w.blocking ? 'err' : 'warn'}
                                glyph="▲"
                                title={`${tooltipFor(DRAFT_WARNING_LABELS, w.code)}\n\n${w.message}`}
                            >
                                {labelFor(DRAFT_WARNING_LABELS, w.code)}
                            </Badge>
                        </li>
                    ))}
                </ul>
            )}

            <p className="mt-3 text-xs text-muted">
                {/* NO approve button (K2). The link is the whole control. */}
                <Link to={`/approvals?draft=${draft.id}`} className={TEXT_LINK}>
                    Open this draft in Approvals
                </Link>
                {' — '}
                {held
                    ? 'that is where the wording, the document and the recipient can be changed, and where the draft can be returned to the queue.'
                    : 'that is the one place a reply is read and approved.'}
            </p>

            {/* Only for a REJECTED or FAILED draft — the two states compose-draft.ts
                does not refuse. A draft waiting for approval is shown above, and
                that IS the reason there is no button here. */}
            {compose && <div className="mt-3 border-t border-hair-2 pt-3">{compose}</div>}
        </Card>
    );
}

/**
 * THE STATE THAT USED TO RENDER NOTHING (P37b, Roman's decision K10).
 *
 * Seven tickets on 2026-09-30 carried a request the agent deliberately did not
 * answer — `chooseTemplate` skips rather than telling a customer a document does
 * not exist — and one of them (`DRA-2026-00014`) is a real customer mail. On
 * their screens the outbound half of the request was blank, which is why the
 * Case File tab looked like the place to answer from.
 *
 * So this branch says three things and no more: nothing has been written, a note
 * is not a reply, and here is the button. The sentence about what the button
 * does names the approval, because that is the promise the whole system rests
 * on — a reply started here is still a draft.
 */
function NoReplyCard({ ticket, canReply, composeAction }: {
    ticket: Ticket; canReply: boolean; composeAction?: ReactNode;
}) {
    const { toast } = useToast();
    const navigate = useNavigate();
    const start = useStartReply(ticket.ticketNumber);

    return (
        <Card
            title="Reply"
            subtitle="Nothing has been written for this request yet"
            action={<ReplyStateBadge state="None" />}
        >
            <p className="text-xs text-muted">
                The agent writes a reply when it has a document to deliver or a refusal to pass on. Where it has
                neither it stops and leaves the answer to a person — that is what this ticket is waiting for.
            </p>

            {/* The ordinary route first, and it holds the view's one primary (P39):
                ask the agent to search the archive and write from the approved
                template. The hand-written reply below is the exception, for a
                request the agent has nothing to answer with — which is the case
                Katia's mail of 2026-09-28 was about. */}
            {composeAction && <div className="mt-3">{composeAction}</div>}

            {canReply ? (
                <>
                    <div className={composeAction ? 'mt-4 border-t border-hair-2 pt-3' : 'mt-3'}>
                        <Button
                            variant={composeAction ? 'secondary' : 'primary'}
                            size="sm"
                            disabled={start.isPending}
                            onClick={() => start.mutate(undefined, {
                                onSuccess: (result) => {
                                    toast(
                                        `Draft ${result.draftId} started for ${ticket.ticketNumber} — write the answer `
                                        + 'here and approve it. Nothing has been sent.',
                                        'success',
                                        { sticky: true },
                                    );
                                    // P37a's deep link. The editor, the recipient, the
                                    // archive picker and the approval are all on that
                                    // screen; this card deliberately holds none of them.
                                    navigate(`/approvals?draft=${result.draftId}`);
                                },
                                onError: (err: unknown) => toast(
                                    err instanceof Error ? err.message : 'Could not start the reply', 'error',
                                ),
                            })}
                        >
                            {start.isPending ? 'Starting…' : 'Write a reply to the customer'}
                        </Button>
                    </div>
                    <p className="mt-2 text-xs text-muted">
                        Creates an <strong>empty</strong> draft addressed to the customer — the address comes from
                        the request itself, not from you — and opens it in{' '}
                        <Link to="/approvals" className={TEXT_LINK}>Approvals</Link>, where you type what they should
                        be told. Nothing is sent until somebody approves it there, and a reply with no text cannot be
                        approved in a batch. A note on the Case File below is something else: it stays inside the
                        house and never reaches the customer.
                    </p>
                </>
            ) : (
                <p className="mt-3 text-xs text-muted">
                    Writing a reply needs edit rights on Tickets. A note on the Case File below stays inside the
                    house and is never sent.
                </p>
            )}
        </Card>
    );
}
